import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import { 
  CheckIcon,
  BoltIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ChartBarIcon,
  SparklesIcon,
  BuildingOfficeIcon,
  ArrowRightIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

// Mock FAQ data
const faqs = [
  {
    question: "What happens after my free trial?",
    answer: "After your 7-day free trial, you'll be automatically charged for your chosen plan unless you cancel. You can cancel anytime during the trial period."
  },
  {
    question: "Can I change plans later?",
    answer: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and we'll prorate any charges."
  },
  {
    question: "How many users can access my account?",
    answer: "All plans include unlimited users from your organization. Simply invite team members after you sign up."
  },
  {
    question: "Do I need to install anything in Salesforce?",
    answer: "No installation required! FlowDocs connects via Salesforce's secure OAuth - you just click to authorize and we handle the rest."
  }
];

export default function PlansPageRedesign() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [selectedPlan, setSelectedPlan] = useState<string>('professional');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const handleGetStarted = (plan: string) => {
    if (!user) {
      // Store selected plan in sessionStorage so it can be used after signup
      sessionStorage.setItem('selectedPlan', plan);
      navigate('/signup');
    } else {
      navigate(`/upgrade?plan=${plan}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onSignOut={signOut} isPublic={!user} />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Start with a 7-day free trial. No credit card required. 
              Choose the plan that fits your team's needs.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center text-sm text-gray-600">
                <ShieldCheckIcon className="w-5 h-5 mr-2 text-green-600" />
                SOC 2 Compliant
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <CreditCardIcon className="w-5 h-5 mr-2 text-green-600" />
                Secure Stripe Payments
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <ArrowPathIcon className="w-5 h-5 mr-2 text-green-600" />
                Cancel Anytime
              </div>
            </div>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center">
              <span className={`mr-3 ${billingCycle === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                type="button"
                className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 bg-blue-600"
                role="switch"
                aria-checked={billingCycle === 'annual'}
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    billingCycle === 'annual' ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`ml-3 ${billingCycle === 'annual' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Annual
                <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  Save 17%
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Starter Plan */}
          <div
            className={`relative bg-white rounded-2xl shadow-lg p-8 cursor-pointer transition-all hover:shadow-xl ${
              selectedPlan === 'starter' ? 'ring-2 ring-blue-600' : ''
            }`}
            onClick={() => setSelectedPlan('starter')}
          >
            {/* Basic Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium text-white" style={{ backgroundColor: '#FFBC06' }}>
                Basic
              </span>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900">Starter</h3>
              <p className="mt-2 text-gray-600">Essential flow documentation for small teams</p>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline">
                <span className="text-5xl font-bold text-gray-900">
                  ${billingCycle === 'annual' ? '82' : '99'}
                </span>
                <span className="ml-2 text-gray-500">/month</span>
              </div>
              {billingCycle === 'annual' && (
                <p className="mt-1 text-sm text-gray-600">$990 billed annually</p>
              )}
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Unlimited users</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">1 org (production or sandbox)</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Daily automated flow syncs</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Flow health scorecards</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">AI-generated summaries</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Field impact analysis</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Trigger conflict detection</span>
              </li>
            </ul>

            <button
              onClick={() => handleGetStarted('starter')}
              className="w-full py-3 px-4 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              style={{ backgroundColor: '#FFBC06' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5A905'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFBC06'}
            >
              Start Free Trial
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </button>
          </div>

          {/* Standard Plan */}
          <div
            className={`relative bg-white rounded-2xl shadow-lg p-8 cursor-pointer transition-all hover:shadow-xl ${
              selectedPlan === 'standard' ? 'ring-2 ring-blue-600' : ''
            }`}
            onClick={() => setSelectedPlan('standard')}
          >
            {/* Value Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-green-600 text-white">
                Value
              </span>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900">Standard</h3>
              <p className="mt-2 text-gray-600">Perfect for teams getting started with flow documentation</p>
            </div>
            
            <div className="mb-8">
              <div className="flex items-baseline">
                <span className="text-5xl font-bold text-gray-900">
                  ${billingCycle === 'annual' ? '166' : '199'}
                </span>
                <span className="ml-2 text-gray-500">/month</span>
              </div>
              {billingCycle === 'annual' && (
                <p className="mt-1 text-sm text-gray-600">$1,990 billed annually</p>
              )}
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Unlimited users</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">1 environment (up to 5 orgs per environment)</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Daily automated flow syncs</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Flow health scorecards</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Field impact analysis</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Trigger conflict detection</span>
              </li>
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Basic AI flow summaries</span>
              </li>
            </ul>

            <button
              onClick={() => handleGetStarted('standard')}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              Start Free Trial
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </button>
          </div>

          {/* Professional Plan */}
          <div
            className={`relative bg-white rounded-2xl shadow-lg p-8 cursor-pointer transition-all hover:shadow-xl ${
              selectedPlan === 'professional' ? 'ring-2 ring-blue-600' : ''
            }`}
            onClick={() => setSelectedPlan('professional')}
          >
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-blue-600 text-white">
                Most Popular
              </span>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900">Professional</h3>
              <p className="mt-2 text-gray-600">Advanced features for growing Salesforce teams</p>
            </div>
            
            <div className="mb-8">
              <div className="flex items-baseline">
                <span className="text-5xl font-bold text-gray-900">
                  ${billingCycle === 'annual' ? '249' : '299'}
                </span>
                <span className="ml-2 text-gray-500">/month</span>
              </div>
              {billingCycle === 'annual' && (
                <p className="mt-1 text-sm text-gray-600">$2,990 billed annually</p>
              )}
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Everything in Standard, plus:</span>
              </li>
              <li className="flex items-start">
                <SparklesIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Export documentation as PDFs</span>
              </li>
              <li className="flex items-start">
                <ArrowPathIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">On-demand flow syncs</span>
              </li>
              <li className="flex items-start">
                <BuildingOfficeIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Additional environments available ($99/environment)</span>
              </li>
              <li className="flex items-start">
                <ChartBarIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Advanced analytics & insights</span>
              </li>
              <li className="flex items-start">
                <BoltIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Priority support</span>
              </li>
            </ul>

            <button
              onClick={() => handleGetStarted('professional')}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              Start Free Trial
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 max-w-5xl mx-auto">
          <div className="bg-gray-900 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Need an Enterprise Solution?</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Custom user limits, dedicated support, and flexible pricing for large organizations
            </p>
            <button 
              onClick={() => window.location.href = 'mailto:sales@getflowdocs.com?subject=Enterprise Plan Inquiry'}
              className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Contact Sales
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Feature Comparison */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Compare Plans
        </h2>
        
        <div className="overflow-x-auto">
          <table className="w-full max-w-5xl mx-auto">
            <thead>
              <tr className="border-b">
                <th className="text-left py-4 px-4 font-medium text-gray-900">Features</th>
                <th className="text-center py-4 px-4">
                  <div className="font-medium text-gray-900">Starter</div>
                  <div className="text-sm text-gray-500">${billingCycle === 'annual' ? '82' : '99'}/mo</div>
                </th>
                <th className="text-center py-4 px-4">
                  <div className="font-medium text-gray-900">Standard</div>
                  <div className="text-sm text-gray-500">${billingCycle === 'annual' ? '166' : '199'}/mo</div>
                </th>
                <th className="text-center py-4 px-4">
                  <div className="font-medium text-gray-900">Professional</div>
                  <div className="text-sm text-gray-500">${billingCycle === 'annual' ? '249' : '299'}/mo</div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="py-4 px-4 text-gray-700">Team Members</td>
                <td className="text-center py-4 px-4">Unlimited</td>
                <td className="text-center py-4 px-4">Unlimited</td>
                <td className="text-center py-4 px-4">Unlimited</td>
              </tr>
              <tr>
                <td className="py-4 px-4 text-gray-700">Orgs/Environments</td>
                <td className="text-center py-4 px-4">1 org</td>
                <td className="text-center py-4 px-4">1 environment (5 orgs)</td>
                <td className="text-center py-4 px-4">1 environment (+$99 add-ons)</td>
              </tr>
              <tr>
                <td className="py-4 px-4 text-gray-700">Flow Sync Frequency</td>
                <td className="text-center py-4 px-4">Daily</td>
                <td className="text-center py-4 px-4">Daily</td>
                <td className="text-center py-4 px-4">Daily + On-demand</td>
              </tr>
              <tr>
                <td className="py-4 px-4 text-gray-700">AI Summaries</td>
                <td className="text-center py-4 px-4">Basic</td>
                <td className="text-center py-4 px-4">Basic</td>
                <td className="text-center py-4 px-4">Advanced</td>
              </tr>
              <tr>
                <td className="py-4 px-4 text-gray-700">PDF Export</td>
                <td className="text-center py-4 px-4">—</td>
                <td className="text-center py-4 px-4">—</td>
                <td className="text-center py-4 px-4">
                  <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                </td>
              </tr>
              <tr>
                <td className="py-4 px-4 text-gray-700">Support</td>
                <td className="text-center py-4 px-4">Standard</td>
                <td className="text-center py-4 px-4">Standard</td>
                <td className="text-center py-4 px-4">Priority</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <QuestionMarkCircleIcon 
                    className={`w-5 h-5 text-gray-400 transform transition-transform ${
                      expandedFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <button 
              onClick={() => navigate('/help/contact-support')}
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              Contact our team
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}